import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPosOpenQrBootstrap } from '../api';
import { detectThemeSync, watchThemeChanges } from '../../../utils/themeUtils';
import {
  buildCartKey,
  formatCurrency,
  getCartCount,
  getCartSubtotal,
  getCartRows,
  getItemVariationOptions,
  getOpenOrderSession,
  getResolvedItemVariation,
  updateOpenOrderSession,
} from '../openOrderUtils';

const ACCENT = '#4ce1b6';
const ALL_CATEGORY_ID = '__all__';
const UNCATEGORIZED_CATEGORY_ID = '__uncategorized__';
/** Leaf category id from menu item (FK), or uncategorized token. */
const getMenuItemLeafCategoryId = (item) => (
  item?.category === null || item?.category === undefined || item?.category === ''
    ? UNCATEGORIZED_CATEGORY_ID
    : String(item.category)
);

const getDietaryIndicatorStyles = (dietaryValue) => {
  const normalized = String(dietaryValue || '').toLowerCase();
  const isVeg = ['veg', 'vegan', 'jain'].includes(normalized);
  return {
    color: isVeg ? '#22c55e' : '#ef4444',
    isVeg,
  };
};

export default function PosOpenOrderingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bootstrap, setBootstrap] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [cart, setCart] = useState({});
  const [variationDialog, setVariationDialog] = useState({
    open: false,
    item: null,
    selectedVariationId: '',
  });
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const isDark = currentTheme === 'dark';
  const isDesktopMenu = useMediaQuery('(min-width:900px)');

  useEffect(() => {
    const cleanup = watchThemeChanges((theme) => setCurrentTheme(theme));
    return cleanup;
  }, []);

  const queryParams = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return {
      hotelId: q.get('hotel_id') || '',
      targetType: q.get('target_type') || '',
      targetId: q.get('target_id') || '',
      qrToken: q.get('qr_token') || '',
    };
  }, [location.search]);

  const loadBootstrap = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await getPosOpenQrBootstrap(queryParams);
      setBootstrap(payload);
    } catch (err) {
      setError(err?.userMessage || 'Unable to load QR ordering page.');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    loadBootstrap();
  }, [loadBootstrap]);

  useEffect(() => {
    const session = getOpenOrderSession(queryParams);
    setCart(session.cart || {});
    setSessionLoaded(true);
  }, [queryParams]);

  useEffect(() => {
    if (!sessionLoaded) return;
    updateOpenOrderSession(queryParams, { cart });
  }, [cart, queryParams, sessionLoaded]);

  /** Room QR: persist guest prefill to session so checkout fields load from booking guest. */
  useEffect(() => {
    if (!bootstrap || !sessionLoaded) return;
    if (bootstrap.target_type !== 'room' || !bootstrap.guest_prefill) return;
    const pre = bootstrap.guest_prefill;
    const session = getOpenOrderSession(queryParams);
    const cf = session.customerForm || {};
    const nameEmpty = !String(cf.customer_name || '').trim();
    const phoneEmpty = !String(cf.customer_phone || '').trim();
    if (!nameEmpty && !phoneEmpty) return;
    updateOpenOrderSession(queryParams, {
      customerForm: {
        ...cf,
        customer_name: nameEmpty ? String(pre.customer_name || '').trim() : cf.customer_name,
        customer_phone_country_code: pre.customer_phone_country_code || cf.customer_phone_country_code || '+91',
        customer_phone: phoneEmpty
          ? String(pre.customer_phone || '').replace(/\D/g, '').slice(0, 10)
          : cf.customer_phone,
      },
    });
  }, [bootstrap, sessionLoaded, queryParams]);

  const categories = useMemo(() => (bootstrap?.categories || []), [bootstrap]);
  const categoryById = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => {
      if (c && c.id != null) map.set(String(c.id), c);
    });
    return map;
  }, [categories]);

  /** Top-level categories only (no parent) — used for chips / filter, not subcategories. */
  const parentCategories = useMemo(() => (
    categories
      .filter((c) => c && (c.parent == null || c.parent === ''))
      .slice()
      .sort((a, b) => {
        const ao = Number(a.sort_order ?? 0);
        const bo = Number(b.sort_order ?? 0);
        if (ao !== bo) return ao - bo;
        return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
      })
  ), [categories]);

  const menuItems = useMemo(() => (bootstrap?.menu_items || []), [bootstrap]);

  const getParentCategoryIdForItem = useCallback((item) => {
    const leaf = getMenuItemLeafCategoryId(item);
    if (leaf === UNCATEGORIZED_CATEGORY_ID) return UNCATEGORIZED_CATEGORY_ID;
    const cat = categoryById.get(leaf);
    if (!cat) return UNCATEGORIZED_CATEGORY_ID;
    if (cat.parent != null && cat.parent !== '') return String(cat.parent);
    return String(leaf);
  }, [categoryById]);

  const hasUncategorizedItems = useMemo(
    () => menuItems.some((item) => getParentCategoryIdForItem(item) === UNCATEGORIZED_CATEGORY_ID),
    [menuItems, getParentCategoryIdForItem]
  );

  const displayCategories = useMemo(() => (
    [
      { id: ALL_CATEGORY_ID, name: 'All' },
      ...parentCategories,
      ...(hasUncategorizedItems ? [{ id: UNCATEGORIZED_CATEGORY_ID, name: 'Others' }] : []),
    ]
  ), [parentCategories, hasUncategorizedItems]);
  const variationLabelMap = useMemo(() => new Map(
    (bootstrap?.menu_variations || []).map((variation) => [
      Number(variation.id),
      variation.online_display_name || variation.name || `Variation ${variation.id}`,
    ])
  ), [bootstrap]);

  useEffect(() => {
    if (!displayCategories.length) return;
    const hasActiveCategory = displayCategories.some((category) => String(category.id) === String(selectedCategory));
    if (!selectedCategory || !hasActiveCategory) {
      setSelectedCategory(ALL_CATEGORY_ID);
    }
  }, [displayCategories, selectedCategory]);

  const filteredItems = useMemo(() => {
    if (!selectedCategory) return [];
    const term = String(search || '').trim().toLowerCase();
    return menuItems.filter((item) => {
      if (selectedCategory === ALL_CATEGORY_ID) {
        if (!term) return true;
        const haystack = `${item?.name || ''} ${item?.description || ''} ${item?.category_name || ''}`.toLowerCase();
        return haystack.includes(term);
      }
      const parentId = getParentCategoryIdForItem(item);
      if (selectedCategory === UNCATEGORIZED_CATEGORY_ID) {
        if (parentId !== UNCATEGORIZED_CATEGORY_ID) return false;
      } else if (parentId !== String(selectedCategory)) {
        return false;
      }
      if (!term) return true;
      const haystack = `${item?.name || ''} ${item?.description || ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [menuItems, search, selectedCategory, getParentCategoryIdForItem]);

  const groupedMenuSections = useMemo(() => {
    const groups = new Map();
    filteredItems.forEach((item) => {
      const parentId = getParentCategoryIdForItem(item);
      if (!groups.has(parentId)) {
        groups.set(parentId, []);
      }
      groups.get(parentId).push(item);
    });

    let orderedCategoryIds;
    if (selectedCategory === ALL_CATEGORY_ID) {
      orderedCategoryIds = [
        ...parentCategories.map((c) => String(c.id)).filter((id) => (groups.get(id) || []).length > 0),
        ...((groups.get(UNCATEGORIZED_CATEGORY_ID) || []).length > 0 ? [UNCATEGORIZED_CATEGORY_ID] : []),
      ];
    } else {
      orderedCategoryIds = [String(selectedCategory)];
    }

    return orderedCategoryIds
      .map((categoryId) => {
        const categoryMeta = displayCategories.find((category) => String(category.id) === String(categoryId));
        const fromMap = categoryById.get(String(categoryId));
        return {
          id: categoryId,
          name: categoryMeta?.name || fromMap?.name || 'Menu',
          items: groups.get(categoryId) || [],
        };
      })
      .filter((section) => section.items.length > 0);
  }, [
    categoryById,
    displayCategories,
    filteredItems,
    getParentCategoryIdForItem,
    parentCategories,
    selectedCategory,
  ]);

  const cartSubtotal = useMemo(() => getCartSubtotal(cart), [cart]);
  const cartCount = useMemo(() => getCartCount(cart), [cart]);
  const hasCartItems = cartCount > 0;

  const addMenuItemToCart = useCallback((item, explicitVariationId) => {
    const variation = getResolvedItemVariation(item, explicitVariationId, variationLabelMap);
    const key = buildCartKey(item.id, variation.variationId);
    setCart((prev) => {
      const current = prev[key];
      const nextQty = Number(current?.quantity || 0) + 1;
      return {
        ...prev,
        [key]: {
          key,
          menuItemId: item.id,
          name: item.name,
          variationId: variation.variationId,
          variationLabel: variation.variationLabel,
          unitPrice: Number(variation.unitPrice || item?.price || 0),
          quantity: nextQty,
        },
      };
    });
  }, [variationLabelMap]);

  const getTotalQtyForMenuItem = useCallback(
    (item) => getCartRows(cart)
      .filter((row) => row.menuItemId === item.id)
      .reduce((sum, row) => sum + Number(row.quantity || 0), 0),
    [cart]
  );

  const updateCartLineQuantity = useCallback((item, variationId, delta) => {
    const variation = getResolvedItemVariation(item, variationId, variationLabelMap);
    const key = buildCartKey(item.id, variation.variationId);
    setCart((prev) => {
      const current = prev[key];
      const currentQty = Number(current?.quantity || 0);
      const nextQty = Math.max(0, currentQty + delta);
      if (nextQty === 0) {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [key]: {
          key,
          menuItemId: item.id,
          name: item.name,
          variationId: variation.variationId,
          variationLabel: variation.variationLabel,
          unitPrice: Number(variation.unitPrice || item?.price || 0),
          quantity: nextQty,
        },
      };
    });
  }, [variationLabelMap]);

  const openVariationDialog = useCallback((item) => {
    const opts = getItemVariationOptions(item, variationLabelMap);
    if (!opts.length) {
      addMenuItemToCart(item);
      return;
    }
    setVariationDialog({
      open: true,
      item,
      selectedVariationId: String(opts[0].variationId ?? ''),
    });
  }, [addMenuItemToCart, variationLabelMap]);

  const closeVariationDialog = useCallback(() => {
    setVariationDialog({ open: false, item: null, selectedVariationId: '' });
  }, []);

  const confirmVariationDialog = useCallback(() => {
    const { item, selectedVariationId } = variationDialog;
    if (!item || selectedVariationId === '') {
      closeVariationDialog();
      return;
    }
    addMenuItemToCart(item, selectedVariationId);
    closeVariationDialog();
  }, [addMenuItemToCart, closeVariationDialog, variationDialog]);

  const requestAddOrIncrement = useCallback((item) => {
    const opts = getItemVariationOptions(item, variationLabelMap);
    if (opts.length > 0) {
      openVariationDialog(item);
      return;
    }
    addMenuItemToCart(item);
  }, [addMenuItemToCart, openVariationDialog, variationLabelMap]);

  const decrementMenuItem = useCallback((item) => {
    const rows = getCartRows(cart).filter((row) => row.menuItemId === item.id && row.quantity > 0);
    if (!rows.length) return;
    const { variationId } = rows[0];
    updateCartLineQuantity(item, variationId, -1);
  }, [cart, updateCartLineQuantity]);

  const openCheckoutPage = useCallback(() => {
    updateOpenOrderSession(queryParams, { cart });
    navigate({
      pathname: '/pos-restaurant/open/checkout/',
      search: location.search,
    });
  }, [cart, location.search, navigate, queryParams]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: isDark ? '#0b1220' : '#f8fbff' }}>
        <Stack spacing={1} alignItems="center">
          <CircularProgress size={34} />
          <Typography variant="body2" color="text.secondary">Loading open ordering...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', p: 2.4, bgcolor: isDark ? '#0b1220' : '#f8fbff' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!bootstrap) return null;

  const hotelImageUrl = String(bootstrap?.hotel?.image_url || '').trim();
  const hotelImageAlt = String(bootstrap?.hotel?.name || 'Hotel').trim() || 'Hotel';
  const targetType = bootstrap?.target_type;
  const target = bootstrap?.target || {};
  const targetLabel = targetType === 'room'
    ? `Room ${target?.room_number || '-'}`
    : (target?.name || `Table ${target?.id || '-'}`);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 0.75, sm: 1.25, md: 2 },
        pt: 0,
        pb: hasCartItems
          ? {
            xs: 'calc(108px + env(safe-area-inset-bottom, 0px))',
            sm: 'calc(94px + env(safe-area-inset-bottom, 0px))',
          }
          : { xs: 2.4, sm: 3 },
        bgcolor: isDark ? '#0b1220' : '#f3fbfa',
        color: isDark ? '#eaf6ff' : '#13233b',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 'none',
          mx: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mx: { xs: -0.75, sm: -1.25, md: -2 },
            p: { xs: 1.2, sm: 1.5 },
            borderRadius: 0,
            border: `1px solid ${alpha(ACCENT, 0.34)}`,
            borderLeft: 'none',
            borderRight: 'none',
            background: isDark
              ? `linear-gradient(160deg, ${alpha('#07192b', 0.96)} 20%, ${alpha(ACCENT, 0.2)} 100%)`
              : `linear-gradient(160deg, ${alpha('#ffffff', 0.98)} 20%, ${alpha(ACCENT, 0.22)} 100%)`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} justifyContent="space-between" sx={{ width: '100%' }}>
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: { xs: 64, sm: 72, md: 80 },
                  height: { xs: 64, sm: 72, md: 80 },
                  minWidth: { xs: 64, sm: 72, md: 80 },
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(ACCENT, 0.38)}`,
                  bgcolor: isDark ? alpha('#07192b', 0.95) : alpha('#ffffff', 0.96),
                  display: 'grid',
                  placeItems: 'center',
                  overflow: 'hidden',
                  boxShadow: isDark ? '0 4px 18px rgba(0,0,0,0.4)' : '0 2px 14px rgba(0,0,0,0.1)',
                  flexShrink: 0,
                }}
              >
                {hotelImageUrl ? (
                  <Box
                    component="img"
                    src={hotelImageUrl}
                    alt={hotelImageAlt}
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      p: 0.25,
                    }}
                  />
                ) : (
                  <>
                    {targetType === 'room' ? (
                      <RoomServiceIcon sx={{ fontSize: 44, color: ACCENT, opacity: 0.9 }} />
                    ) : (
                      <TableRestaurantIcon sx={{ fontSize: 44, color: ACCENT, opacity: 0.9 }} />
                    )}
                  </>
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.1,
                    fontSize: { xs: 16.5, sm: 18 },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: { xs: 180, sm: 320, md: 420 },
                  }}
                >
                  {hotelImageAlt}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.3,
                    fontSize: { xs: 12.5, sm: 13.5 },
                    fontWeight: 600,
                    color: isDark ? '#9fbbd2' : '#607086',
                    letterSpacing: '0.02em',
                  }}
                >
                  Open ordering
                </Typography>
              </Box>
            </Stack>

            <Chip
              icon={targetType === 'room'
                ? <RoomServiceIcon sx={{ fontSize: { xs: 26, sm: 30 } }} />
                : <TableRestaurantIcon sx={{ fontSize: { xs: 26, sm: 30 } }} />}
              label={targetLabel}
              sx={{
                height: { xs: 56, sm: 62 },
                flexShrink: 0,
                bgcolor: isDark ? alpha('#0f172a', 0.78) : alpha('#ffffff', 0.92),
                border: `1px solid ${alpha(ACCENT, 0.42)}`,
                fontWeight: 800,
                fontSize: { xs: '1.08rem', sm: '1.18rem' },
                borderRadius: 2,
                '& .MuiChip-label': {
                  px: { xs: 1.15, sm: 1.45 },
                  maxWidth: { xs: 200, sm: 280 },
                },
              }}
            />
          </Stack>
        </Paper>

        <Stack spacing={1.2} sx={{ mt: 1.2 }}>
          <TextField
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search menu items..."
            fullWidth
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ fontSize: 18, mr: 0.8, color: isDark ? '#9fbbd2' : '#607086' }} />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.6,
                bgcolor: isDark ? alpha('#0f1d30', 0.82) : alpha('#ffffff', 0.98),
                border: `1px solid ${alpha(ACCENT, 0.22)}`,
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              gap: 1,
              width: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              pb: 0.25,
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { height: 6 },
            }}
          >
            {displayCategories.map((category) => {
              const categoryId = String(category.id);
              const isActive = categoryId === String(selectedCategory);
              return (
                <Chip
                  key={category.id}
                  label={category.name}
                  onClick={() => setSelectedCategory(categoryId)}
                  sx={{
                    flexShrink: 0,
                    height: 34,
                    px: 0.25,
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 13,
                    ...(isActive
                      ? {
                        bgcolor: ACCENT,
                        color: '#0b1620',
                        border: 'none',
                        '&:hover': { bgcolor: alpha(ACCENT, 0.92) },
                      }
                      : {
                        bgcolor: isDark ? alpha('#0f172a', 0.95) : '#ffffff',
                        color: isDark ? '#f1f5f9' : '#162c43',
                        border: `1px solid ${isDark ? alpha('#fff', 0.2) : alpha('#000', 0.12)}`,
                        '&:hover': {
                          bgcolor: isDark ? alpha('#1e293b', 0.95) : alpha('#f8fafc', 1),
                        },
                      }),
                  }}
                />
              );
            })}
          </Box>

          {groupedMenuSections.map((section, sectionIndex) => (
            <Stack key={section.id} spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 0.2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: 16, sm: 18 } }}>
                  {section.name}
                </Typography>
                <Typography sx={{ fontWeight: 700, color: isDark ? '#9fbbd2' : '#607086' }}>
                  ({section.items.length})
                </Typography>
              </Stack>

              <Stack spacing={0} sx={{ width: '100%' }}>
                {section.items.map((item) => {
                  const variationOptions = getItemVariationOptions(item, variationLabelMap);
                  const hasVariations = variationOptions.length > 0;
                  const currency = bootstrap?.hotel?.currency;
                  const minVarPrice = hasVariations
                    ? Math.min(...variationOptions.map((o) => o.unitPrice))
                    : null;
                  const itemQuantity = getTotalQtyForMenuItem(item);
                  const dietaryMeta = getDietaryIndicatorStyles(item?.dietary);
                  const menuImageUrl = String(item?.menu_image_url || '').trim();

                  return (
                    <Box key={`${section.id}-${item.id}`}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 0.75, md: 0.9 },
                          borderRadius: { xs: 1.4, md: 0 },
                          border: { xs: `1px solid ${alpha(ACCENT, 0.2)}`, md: 'none' },
                          borderBottom: { md: `1px solid ${alpha(ACCENT, 0.16)}` },
                          bgcolor: isDark ? alpha('#0f1b2c', 0.88) : '#ffffff',
                          boxShadow: 'none',
                        }}
                      >
                        <Stack
                          direction={{ xs: 'row', md: 'row' }}
                          spacing={{ xs: 0.8, md: 1.1 }}
                          alignItems="stretch"
                          sx={{
                            flexDirection: { xs: 'row', md: 'row' },
                            width: '100%',
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: 72, md: 82 },
                              minHeight: { xs: 72, md: 82 },
                              alignSelf: 'stretch',
                              position: 'relative',
                              borderRadius: 1.1,
                              border: `1px solid ${alpha(ACCENT, 0.28)}`,
                              bgcolor: isDark ? alpha('#091423', 0.9) : alpha('#f3fffb', 0.98),
                              overflow: 'hidden',
                              flexShrink: 0,
                            }}
                          >
                            {menuImageUrl ? (
                              <Box
                                component="img"
                                src={menuImageUrl}
                                alt={item.name}
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  objectPosition: 'center',
                                  display: 'block',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  p: 0.6,
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: alpha(ACCENT, 0.95),
                                    fontWeight: 800,
                                    fontSize: 11,
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {section.name}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          <Stack
                            spacing={isDesktopMenu ? 0.3 : 0.45}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              width: '100%',
                            }}
                          >
                            {isDesktopMenu ? (
                              <>
                                <Stack
                                  direction="row"
                                  alignItems="flex-start"
                                  justifyContent="space-between"
                                  spacing={2}
                                  sx={{ width: '100%' }}
                                >
                                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                                    <Box
                                      aria-label={dietaryMeta.isVeg ? 'Veg' : 'Non-Veg'}
                                      sx={{
                                        mt: 0.35,
                                        width: 16,
                                        height: 16,
                                        minWidth: 16,
                                        borderRadius: 0.6,
                                        border: `1.4px solid ${dietaryMeta.color}`,
                                        display: 'grid',
                                        placeItems: 'center',
                                        bgcolor: alpha(dietaryMeta.color, 0.08),
                                      }}
                                    >
                                      {dietaryMeta.isVeg ? (
                                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dietaryMeta.color }} />
                                      ) : (
                                        <Box
                                          sx={{
                                            width: 0,
                                            height: 0,
                                            borderLeft: '3.5px solid transparent',
                                            borderRight: '3.5px solid transparent',
                                            borderBottom: `7px solid ${dietaryMeta.color}`,
                                          }}
                                        />
                                      )}
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography sx={{ fontWeight: 800, lineHeight: 1.2, fontSize: 15 }}>
                                        {item.name}
                                      </Typography>
                                      {minVarPrice != null ? (
                                        <Typography sx={{ mt: 0.2 }}>
                                          <Typography component="span" sx={{ fontWeight: 600, color: isDark ? '#9fbbd2' : '#607086', mr: 0.5 }}>
                                            From
                                          </Typography>
                                          <Typography component="span" sx={{ fontWeight: 800, color: ACCENT }}>
                                            {formatCurrency(minVarPrice, currency)}
                                          </Typography>
                                        </Typography>
                                      ) : (
                                        <Typography sx={{ fontWeight: 800, color: ACCENT, mt: 0.2 }}>
                                          {formatCurrency(Number(item?.price || 0), currency)}
                                        </Typography>
                                      )}
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: isDark ? '#b3c7dc' : '#607086',
                                          lineHeight: 1.25,
                                          fontSize: 13,
                                          mt: 0.05,
                                          display: '-webkit-box',
                                          WebkitLineClamp: 1,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {item.description || 'Freshly prepared item.'}
                                      </Typography>
                                    </Box>
                                  </Stack>

                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{ flexShrink: 0, ml: 1 }}
                                  >
                                    {itemQuantity > 0 ? (
                                      <Stack
                                        direction="row"
                                        spacing={0.3}
                                        alignItems="center"
                                        sx={{
                                          px: 0.15,
                                          borderRadius: 1.1,
                                          border: `1px solid ${alpha(ACCENT, 0.45)}`,
                                          bgcolor: isDark ? alpha(ACCENT, 0.16) : alpha(ACCENT, 0.2),
                                          minHeight: 34,
                                        }}
                                      >
                                        <IconButton size="small" onClick={() => decrementMenuItem(item)} sx={{ p: 0.45 }}>
                                          <RemoveIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                        <Typography sx={{ minWidth: 18, textAlign: 'center', fontWeight: 800, fontSize: 14 }}>
                                          {itemQuantity}
                                        </Typography>
                                        <IconButton size="small" onClick={() => requestAddOrIncrement(item)} sx={{ p: 0.45 }}>
                                          <AddIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Stack>
                                    ) : (
                                      <Button
                                        onClick={() => requestAddOrIncrement(item)}
                                        sx={{
                                          minWidth: 84,
                                          minHeight: 34,
                                          textTransform: 'none',
                                          borderRadius: 1.1,
                                          px: 1.25,
                                          py: 0.35,
                                          fontSize: 13,
                                          lineHeight: 1,
                                          fontWeight: 800,
                                          color: isDark ? '#f8b58c' : '#c56a2f',
                                          border: `1px solid ${alpha('#f28b45', 0.75)}`,
                                          bgcolor: isDark ? alpha('#f28b45', 0.08) : alpha('#f28b45', 0.12),
                                          '&:hover': {
                                            bgcolor: isDark ? alpha('#f28b45', 0.14) : alpha('#f28b45', 0.18),
                                          },
                                        }}
                                      >
                                        + Add
                                      </Button>
                                    )}
                                  </Stack>
                                </Stack>
                              </>
                            ) : (
                              <>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.6}>
                                  <Typography sx={{ fontWeight: 800, lineHeight: 1.2, fontSize: 16 }}>
                                    {item.name}
                                  </Typography>
                                  <Box
                                    aria-label={dietaryMeta.isVeg ? 'Veg' : 'Non-Veg'}
                                    sx={{
                                      mt: 0.2,
                                      width: 16,
                                      height: 16,
                                      minWidth: 16,
                                      borderRadius: 0.6,
                                      border: `1.4px solid ${dietaryMeta.color}`,
                                      display: 'grid',
                                      placeItems: 'center',
                                      bgcolor: alpha(dietaryMeta.color, 0.08),
                                    }}
                                  >
                                    {dietaryMeta.isVeg ? (
                                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dietaryMeta.color }} />
                                    ) : (
                                      <Box
                                        sx={{
                                          width: 0,
                                          height: 0,
                                          borderLeft: '3.5px solid transparent',
                                          borderRight: '3.5px solid transparent',
                                          borderBottom: `7px solid ${dietaryMeta.color}`,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Stack>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isDark ? '#b3c7dc' : '#607086',
                                    lineHeight: 1.2,
                                    fontSize: 12.5,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    minHeight: 0,
                                  }}
                                >
                                  {item.description || 'Freshly prepared item.'}
                                </Typography>

                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                  {minVarPrice != null ? (
                                    <Typography sx={{ fontWeight: 900, color: ACCENT }}>
                                      From {formatCurrency(minVarPrice, currency)}
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ fontWeight: 900, color: ACCENT }}>
                                      {formatCurrency(Number(item?.price || 0), currency)}
                                    </Typography>
                                  )}
                                  {itemQuantity > 0 ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.3}
                                      alignItems="center"
                                      sx={{
                                        px: 0.15,
                                        borderRadius: 1.1,
                                        border: `1px solid ${alpha(ACCENT, 0.45)}`,
                                        bgcolor: isDark ? alpha(ACCENT, 0.16) : alpha(ACCENT, 0.2),
                                        minHeight: 34,
                                      }}
                                    >
                                      <IconButton size="small" onClick={() => decrementMenuItem(item)} sx={{ p: 0.45 }}>
                                        <RemoveIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                      <Typography sx={{ minWidth: 18, textAlign: 'center', fontWeight: 800, fontSize: 14 }}>
                                        {itemQuantity}
                                      </Typography>
                                      <IconButton size="small" onClick={() => requestAddOrIncrement(item)} sx={{ p: 0.45 }}>
                                        <AddIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Stack>
                                  ) : (
                                    <Button
                                      onClick={() => requestAddOrIncrement(item)}
                                      sx={{
                                        minWidth: 84,
                                        minHeight: 34,
                                        textTransform: 'none',
                                        borderRadius: 1.1,
                                        px: 1.25,
                                        py: 0.35,
                                        fontSize: 13,
                                        lineHeight: 1,
                                        fontWeight: 800,
                                        color: isDark ? '#f8b58c' : '#c56a2f',
                                        border: `1px solid ${alpha('#f28b45', 0.75)}`,
                                        bgcolor: isDark ? alpha('#f28b45', 0.08) : alpha('#f28b45', 0.12),
                                        '&:hover': {
                                          bgcolor: isDark ? alpha('#f28b45', 0.14) : alpha('#f28b45', 0.18),
                                        },
                                      }}
                                    >
                                      + Add
                                    </Button>
                                  )}
                                </Stack>
                              </>
                            )}
                          </Stack>
                        </Stack>
                      </Paper>
                    </Box>
                  );
                })}
              </Stack>

              {sectionIndex < groupedMenuSections.length - 1 ? (
                <Divider sx={{ borderColor: alpha(ACCENT, 0.2), my: 0.2 }} />
              ) : null}
            </Stack>
          ))}

          {displayCategories.length === 0 ? <Alert severity="info">No menu categories are available right now.</Alert> : null}
          {displayCategories.length > 0 && filteredItems.length === 0 ? (
            <Alert severity="info">No menu items found for this category.</Alert>
          ) : null}
        </Stack>
      </Box>

      {hasCartItems ? (
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            width: '100%',
            px: { xs: 1, sm: 2, md: 2.4 },
            pt: { xs: 0.9, sm: 1 },
            pb: { xs: 'calc(10px + env(safe-area-inset-bottom, 0px))', sm: 'calc(10px + env(safe-area-inset-bottom, 0px))' },
            borderTop: `1px solid ${alpha(ACCENT, 0.34)}`,
            borderRadius: 0,
            bgcolor: isDark ? alpha('#0b1626', 0.99) : alpha('#fff8f2', 0.99),
            backdropFilter: 'blur(10px)',
          }}
        >
          <Stack
            direction="row"
            spacing={1.1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, sm: 19 }, color: isDark ? '#f7be97' : '#c56a2f', lineHeight: 1.1 }}>
                {cartCount} {cartCount === 1 ? 'item' : 'items'} in cart
              </Typography>
              <Typography sx={{ mt: 0.15, fontSize: { xs: 12, sm: 13 }, color: isDark ? '#a8bfd7' : '#607086' }}>
                Subtotal: {formatCurrency(cartSubtotal, bootstrap?.hotel?.currency)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={openCheckoutPage}
              sx={{
                textTransform: 'none',
                borderRadius: 1.8,
                minHeight: { xs: 50, sm: 54 },
                minWidth: { xs: 150, sm: 196 },
                px: { xs: 2.25, sm: 3 },
                fontSize: { xs: '1rem', sm: '1.05rem' },
                fontWeight: 900,
                border: `1px solid ${alpha(ACCENT, 0.55)}`,
                background: `linear-gradient(135deg, ${ACCENT} 0%, ${alpha(ACCENT, 0.78)} 100%)`,
                color: '#06251e',
                boxShadow: `0 4px 14px ${alpha(ACCENT, 0.35)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${alpha(ACCENT, 0.95)} 0%, ${alpha(ACCENT, 0.68)} 100%)`,
                  boxShadow: `0 6px 18px ${alpha(ACCENT, 0.42)}`,
                },
              }}
            >
              View Cart
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {variationDialog.open ? (() => {
        const options = variationDialog.item
          ? getItemVariationOptions(variationDialog.item, variationLabelMap)
          : [];
        const selectedOption = options.find(
          (opt) => String(opt.variationId) === String(variationDialog.selectedVariationId)
        );
        const selectedPrice = Number(selectedOption?.unitPrice);
        const basePrice = Number(variationDialog.item?.price || 0);
        const effectiveUnitPrice = Number.isFinite(selectedPrice)
          ? selectedPrice
          : basePrice;
        return (
          <Box
            onClick={closeVariationDialog}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1.5,
              bgcolor: isDark ? 'rgba(7,9,13,0.62)' : 'rgba(15,23,42,0.28)',
              backdropFilter: 'blur(2px)',
            }}
          >
            <Paper
              elevation={0}
              onClick={(event) => event.stopPropagation()}
              sx={{
                width: '100%',
                maxWidth: 420,
                p: 2,
                borderRadius: 2.2,
                border: `1px solid ${isDark ? 'rgba(76,225,182,0.34)' : 'rgba(76,225,182,0.28)'}`,
                background: isDark
                  ? 'linear-gradient(165deg, rgba(34,34,40,0.98) 0%, rgba(24,24,30,0.98) 100%)'
                  : 'linear-gradient(165deg, #ffffff 0%, #f3fffb 100%)',
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 0.6 }}>
                Select Variation
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#b8c3d4' : '#4b5a70', mb: 1.2 }}>
                {variationDialog.item?.name || 'Menu Item'} x 1
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.65 }}>
                Choose Variation
              </Typography>
              <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                {options.map((opt) => {
                  const id = String(opt.variationId);
                  const selected = variationDialog.selectedVariationId === id;
                  return (
                    <Chip
                      key={id}
                      clickable
                      label={`${opt.variationLabel || 'Option'} (${formatCurrency(opt.unitPrice, bootstrap?.hotel?.currency)})`}
                      onClick={() => setVariationDialog((prev) => ({ ...prev, selectedVariationId: id }))}
                      variant={selected ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 700,
                        borderColor: selected
                          ? (isDark ? 'rgba(76,225,182,0.82)' : 'rgba(16,185,129,0.82)')
                          : (isDark ? 'rgba(148,163,184,0.42)' : 'rgba(100,116,139,0.38)'),
                        bgcolor: selected
                          ? (isDark ? 'rgba(76,225,182,0.24)' : 'rgba(16,185,129,0.14)')
                          : 'transparent',
                        color: selected
                          ? (isDark ? '#b8fff0' : '#065f46')
                          : (isDark ? '#e2e8f0' : '#334155'),
                        '& .MuiChip-label': {
                          px: 1.05,
                        },
                      }}
                    />
                  );
                })}
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.9,
                  color: isDark ? '#9fc2ba' : '#136f5b',
                  fontWeight: 700,
                }}
              >
                Final rate: {formatCurrency(effectiveUnitPrice, bootstrap?.hotel?.currency)}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={closeVariationDialog}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={confirmVariationDialog}
                  disabled={!variationDialog.selectedVariationId}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    background: 'linear-gradient(90deg, #4ce1b6 0%, #2dd4a0 100%)',
                    color: '#0b1f18',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #3dd1a8 0%, #22c997 100%)',
                    },
                  }}
                >
                  Add Item
                </Button>
              </Stack>
            </Paper>
          </Box>
        );
      })() : null}
    </Box>
  );
}

